// « Ma progression › Performances » (14/10): pure calculations, read from REALISED sessions only (fixtures + sessions finished in
// Séance). Never from a prescription. Type imports only, so the file runs as is under Node for the targeted checks.
// - A set is the ACTUAL set: a logged set as entered (setLogs), or the legacy uniform line (sets × reps at load) expanded into
//   its identical real sets. A missing load or reps stays missing: never 0, never estimated.
// - Charge max = heaviest load of a realised set, only when every set carries its load.
// - Répétitions = total reps of the realised sets. Volume = Σ load × reps, only when EVERY realised set has both; a partial
//   volume is refused (never presented as a total). Timed sets (unit "s", e.g. gainage) never give reps nor volume.
// - Score = a typed result (unit + protocol). A score metric is one exercise + protocol + unit: only identical ones compare.
import type { DoneExercise, ExerciseScore, Session } from "./suivi-fixtures";

export type MetricKey = "charge" | "reps" | "volume" | `score:${string}`;
export interface Metric { key: MetricKey; label: string; unit: "kg" | "reps" | ExerciseScore["unit"] }
export interface ActualSet { load?: number; reps?: number; seconds?: number }
export interface PerfRecord { s: Session; e: DoneExercise; value: number | undefined }

const scoreKey = (x: ExerciseScore): MetricKey => `score:${x.protocol}|${x.unit}`;
const SCORE_KIND: Record<ExerciseScore["unit"], string> = { s: "Temps", m: "Distance", pts: "Points" };

/** The realised sets of one exercise, or null when the record carries no set (score only, or an incomplete legacy line). */
export function actualSets(e: DoneExercise): ActualSet[] | null {
  const one = (reps: number, load: number | undefined): ActualSet => e.unit === "s" ? { seconds: reps } : { reps, ...(load !== undefined ? { load } : {}) };
  if (e.setLogs) return e.setLogs.length ? e.setLogs.map(x => one(x.reps, x.load)) : null;
  if (e.sets === undefined || e.reps === undefined || e.sets <= 0) return null;
  return Array.from({ length: e.sets }, () => one(e.reps!, e.load));
}

export function chargeMax(e: DoneExercise): number | undefined {
  const sets = actualSets(e);
  if (!sets || sets.some(x => x.reps === undefined || x.load === undefined)) return undefined;
  return Math.max(...sets.map(x => x.load!));
}

export function totalReps(e: DoneExercise): number | undefined {
  const sets = actualSets(e);
  if (!sets || sets.some(x => x.reps === undefined)) return undefined;
  return sets.reduce((n, x) => n + x.reps!, 0);
}

/** « full »: every set has load and reps; « partial »: some loads are known but not all (refused); « none »: no load at all. */
export function volumeOf(e: DoneExercise): { status: "full"; value: number; sets: number } | { status: "partial" | "none" } {
  const sets = actualSets(e);
  if (!sets || sets.some(x => x.reps === undefined)) return { status: "none" };
  const known = sets.filter(x => x.load !== undefined).length;
  if (known === 0) return { status: "none" };
  if (known < sets.length) return { status: "partial" };
  return { status: "full", value: sets.reduce((n, x) => n + x.load! * x.reps!, 0), sets: sets.length };
}

export function metricValue(e: DoneExercise, key: MetricKey): number | undefined {
  if (key === "charge") return chargeMax(e);
  if (key === "reps") return totalReps(e);
  if (key === "volume") { const v = volumeOf(e); return v.status === "full" ? v.value : undefined; }
  return e.score && scoreKey(e.score) === key ? e.score.value : undefined;
}

const byTime = (a: Session, b: Session) => (a.date + a.time).localeCompare(b.date + b.time);

/** Metrics this exercise really has (at least one value in the history), in a stable order. Absent metrics are not offered. */
export function availableMetrics(sessions: Session[], exercise: string): Metric[] {
  const ex = sessions.flatMap(s => (s.exercises ?? []).filter(e => e.name === exercise));
  const out: Metric[] = [];
  if (ex.some(e => chargeMax(e) !== undefined)) out.push({ key: "charge", label: "Charge max", unit: "kg" });
  if (ex.some(e => totalReps(e) !== undefined)) out.push({ key: "reps", label: "Répétitions", unit: "reps" });
  if (ex.some(e => volumeOf(e).status === "full")) out.push({ key: "volume", label: "Volume", unit: "kg" });
  const scores = new Map<MetricKey, ExerciseScore>();
  for (const e of ex) if (e.score && !scores.has(scoreKey(e.score))) scores.set(scoreKey(e.score), e.score);
  for (const [key, x] of scores) out.push({ key, label: `${SCORE_KIND[x.unit]} · ${x.protocol}`, unit: x.unit });
  return out;
}

/** Exercises with at least one available metric, alphabetical (French). */
export const perfExercises = (sessions: Session[]) =>
  [...new Set(sessions.flatMap(s => (s.exercises ?? []).map(e => e.name)))].filter(n => availableMetrics(sessions, n).length > 0).sort((a, b) => a.localeCompare(b, "fr"));

/** One record per realised session holding the exercise, oldest first; value undefined = missing for this metric (a gap). */
export const perfRecords = (sessions: Session[], exercise: string, key: MetricKey): PerfRecord[] =>
  [...sessions].sort(byTime).flatMap(s => (s.exercises ?? []).filter(e => e.name === exercise).slice(0, 1).map(e => ({ s, e, value: metricValue(e, key) })));

/** Previous COMPARABLE value (same exercise and metric, so same protocol and unit for a score) before this record. */
export function previousComparable(all: PerfRecord[], rec: PerfRecord): PerfRecord | undefined {
  const i = all.findIndex(x => x.s.id === rec.s.id);
  for (let j = i - 1; j >= 0; j--) if (all[j]!.value !== undefined) return all[j];
  return undefined;
}

const nb = (n: number) => n.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
const clock = (sec: number) => { const m = Math.floor(sec / 60), s = Math.round(sec % 60); return m ? `${m} min ${String(s).padStart(2, "0")} s` : `${s} s`; };
/** A value with its unit, as read by the member. */
export function formatMetric(v: number, m: Metric): string {
  if (m.unit === "kg") return `${nb(v)} kg`;
  if (m.unit === "reps") return `${nb(v)} ${v > 1 ? "répétitions" : "répétition"}`;
  if (m.unit === "s") return clock(v);
  if (m.unit === "m") return `${nb(v)} m`;
  return `${nb(v)} pts`;
}
/** Signed difference, neutral (no better / worse reading): « +2 kg », « −4 s », « 0 kg ». */
export function formatDelta(delta: number, m: Metric): string {
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
  const abs = Math.abs(delta);
  const text = m.unit === "s" ? (abs >= 60 ? clock(abs) : `${nb(abs)} s`) : m.unit === "reps" ? `${nb(abs)} rép.` : m.unit === "kg" ? `${nb(abs)} kg` : m.unit === "m" ? `${nb(abs)} m` : `${nb(abs)} pts`;
  return `${sign}${text}`;
}
/** A score as written in a session line: « 500 m en 1 min 54 s ». */
export const scoreText = (x: ExerciseScore) => x.unit === "s" ? `${x.protocol} en ${clock(x.value)}` : x.unit === "m" ? `${x.protocol} · ${nb(x.value)} m` : `${x.protocol} · ${nb(x.value)} pts`;
